/**
 * FileBlobCache — module-level LRU blob URL cache.
 *
 * Blob URLs are created once per file and reused across previews, thumbnails
 * and downloads. Revoked automatically when the cache reaches capacity.
 *
 * Usage:
 *   import FileBlobCache from './FileBlobCache';
 *   const blobUrl = await FileBlobCache.get(s3Url, cacheKey);
 */

const MAX_ENTRIES = 60; // revoke oldest after this many entries

class FileBlobCacheManager {
  constructor() {
    // Map<cacheKey, { blobUrl, lastUsed }>
    this._cache = new Map();
    // Map<cacheKey, Promise<string>> — in-flight requests
    this._inflight = new Map();
  }

  /**
   * Returns a local blob URL for the given remote URL.
   * If already cached, returns immediately. If another request for the same
   * key is in-flight, waits for that request (no duplicate fetches).
   *
   * @param {string} remoteUrl  - The S3/remote URL to fetch
   * @param {string} [cacheKey] - Stable cache key (e.g. file path). Defaults to remoteUrl.
   */
  async get(remoteUrl, cacheKey) {
    const key = cacheKey || remoteUrl;

    // 1. Already cached
    const cached = this._cache.get(key);
    if (cached) {
      cached.lastUsed = Date.now();
      return cached.blobUrl;
    }

    // 2. In-flight — wait for the existing request
    if (this._inflight.has(key)) {
      return this._inflight.get(key);
    }

    // 3. Fetch fresh
    const promise = this._fetch(remoteUrl, key);
    this._inflight.set(key, promise);
    try {
      const blobUrl = await promise;
      return blobUrl;
    } finally {
      this._inflight.delete(key);
    }
  }

  async _fetch(remoteUrl, key) {
    const res  = await fetch(remoteUrl);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);

    // Evict oldest if over capacity
    if (this._cache.size >= MAX_ENTRIES) {
      let oldestKey = null, oldestTime = Infinity;
      for (const [k, v] of this._cache) {
        if (v.lastUsed < oldestTime) { oldestTime = v.lastUsed; oldestKey = k; }
      }
      if (oldestKey) {
        URL.revokeObjectURL(this._cache.get(oldestKey).blobUrl);
        this._cache.delete(oldestKey);
      }
    }

    this._cache.set(key, { blobUrl, lastUsed: Date.now() });
    return blobUrl;
  }

  /** Invalidate a single entry (e.g. after overwriting a file on save) */
  invalidate(cacheKey) {
    const entry = this._cache.get(cacheKey);
    if (entry) {
      URL.revokeObjectURL(entry.blobUrl);
      this._cache.delete(cacheKey);
    }
  }

  /** Invalidate all entries */
  clear() {
    for (const { blobUrl } of this._cache.values()) URL.revokeObjectURL(blobUrl);
    this._cache.clear();
  }

  /** Pre-warm: fetch multiple URLs in parallel (e.g. visible grid thumbnails) */
  async prefetch(items) {
    await Promise.allSettled(items.map(({ remoteUrl, cacheKey }) => this.get(remoteUrl, cacheKey)));
  }
}

const FileBlobCache = new FileBlobCacheManager();
export default FileBlobCache;
